import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Menú de acciones con tres puntos verticales.
 *
 * @param {Array} options - Array de opciones: [{ label, icon, onClick }]
 */
const ActionMenu = ({ options = [] }) => {
	const [open, setOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
	const buttonRef = useRef(null);
	const menuRef = useRef(null);

	useLayoutEffect(() => {
		if (!open || !buttonRef.current) return undefined;

		const updatePosition = () => {
			const rect = buttonRef.current.getBoundingClientRect();
			const menuWidth = 160;
			const menuItemHeight = 40;
			const menuVerticalPadding = 8;
			const menuHeight = (options.length * menuItemHeight) + menuVerticalPadding;
			const spacing = 8;
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			const canOpenBelow = rect.bottom + spacing + menuHeight <= viewportHeight - spacing;
			const canOpenAbove = rect.top - spacing - menuHeight >= spacing;

			const top = canOpenBelow
				? rect.bottom + spacing
				: canOpenAbove
					? rect.top - menuHeight - spacing
					: Math.max(spacing, viewportHeight - menuHeight - spacing);

			setMenuPosition({
				top,
				left: Math.max(spacing, Math.min(rect.right - menuWidth, viewportWidth - menuWidth - spacing)),
			});
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);

		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [open, options.length]);

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = (event) => {
			const clickedButton = buttonRef.current?.contains(event.target);
			const clickedMenu = menuRef.current?.contains(event.target);

			if (!clickedButton && !clickedMenu) {
				setOpen(false);
			}
		};
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setOpen(false);
				buttonRef.current?.focus();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	return (
		<div className="relative inline-flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-md">
			<button
				ref={buttonRef}
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="flex h-9 w-9 min-h-[36px] min-w-[36px] flex-none items-center justify-center self-center rounded-md border border-line bg-bg text-content shadow-sm hover:bg-surface-2"
				aria-label="Abrir menú de acciones"
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="5" r="2" fill="currentColor" />
					<circle cx="12" cy="12" r="2" fill="currentColor" />
					<circle cx="12" cy="19" r="2" fill="currentColor" />
				</svg>
			</button>

			{open && createPortal(
				<div
					ref={menuRef}
					data-action-menu-portal="true"
					role="menu"
					className="fixed w-40 bg-surface border border-line rounded-lg shadow-lg z-[10002]"
					style={{ top: menuPosition.top, left: menuPosition.left }}
				>
					{options.map((option, index) => (
						<button
							key={index}
							type="button"
							role="menuitem"
							onClick={() => {
								if (option.disabled) {
									return;
								}
								option.onClick();
								setOpen(false);
							}}
							disabled={option.disabled}
							className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
								option.disabled
									? 'cursor-not-allowed text-content-muted'
									: 'text-content hover:bg-surface-2'
							}`}
						>
							{option.icon}
							{option.label}
						</button>
					))}
				</div>,
				document.body
			)}
		</div>
	);
};

export default ActionMenu;
